import { toLocalISODate, toDateInputValue, parseLocalDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/formatCurrency';
import { confirmDialog, reportLoadError, notify } from '../utils/notify';
import React, { useState, useEffect, useContext } from 'react';
import { employeeAPI, contractsAPI } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Briefcase, Building2, Camera, Clock, FolderOpen, IdCard, LayoutGrid, Link, List, Lock, Mail, Paperclip, Pencil, Phone,
  Plus, Power, PowerOff, Receipt, Save, ScrollText, Search, Siren, Trash2, Upload, User, Wallet, X,
} from 'lucide-react';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import { can } from '../utils/permissions';
import PortalAccessSection from './PortalAccessSection';
import './Employees.css';

const Employees = () => {
  const { user } = useContext(AuthContext);
  const { t, locale } = useLanguage();
  const [employees, setEmployees] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('table');   // grid or table
  
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Deductions states
  const [employeeDeductions, setEmployeeDeductions] = useState([]);
  const [availableDeductions, setAvailableDeductions] = useState([]);
  const [showDeductionModal, setShowDeductionModal] = useState(false);
  const [editingDeduction, setEditingDeduction] = useState(null);
  const [deductionForm, setDeductionForm] = useState({
    deduction_id: '',
    custom_amount: '',
    start_date: '',
    end_date: ''
  });

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    date_of_birth: '',
    hire_date: '',
    position: '',
    department: '',
    salary: '',
    currency: 'SRD',
    payment_type: 'monthly',
    hourly_rate: '',
    overtime_rate_multiplier: '1.5',
    sunday_rate_multiplier: '1.5',
    holiday_rate_multiplier: '2.0',
    late_deduction_percent: '10.0', 
    status: 'active',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    id_passport_number: '',
    drivers_license: ''
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const employeePermission = Array.isArray(user?.permissions)
    ? user.permissions.find((p) => p.resource === 'employees') || {}
    : {};
  const canCreateEmployee = employeePermission.can_create === true || ['super_admin', 'hr_manager'].includes(user?.role);
  const canEditEmployee = employeePermission.can_edit === true || ['super_admin', 'hr_manager'].includes(user?.role);
  const canDeleteEmployee = employeePermission.can_delete === true || ['super_admin', 'hr_manager'].includes(user?.role);
  const canManageDeductions = canEditEmployee;

  useEffect(() => {
    loadEmployees();
    loadContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const response = await employeeAPI.getAll();
      setEmployees(response.data.employees);
    } catch (error) {
      console.error('Error loading employees:', error);
      reportLoadError(error, t('Error loading employees'), { main: true });
    } finally {
      setLoading(false);
    }
  };

  const loadContracts = async () => {
    try {
      const response = await contractsAPI.getAll({ status: '' });
      setContracts(response.data.contracts);
    } catch (error) {
      console.error('Error loading contracts:', error);
      reportLoadError(error, t('Error loading contracts'));
    }
  };

  const getEmployeeContract = (employeeId) => {
    return contracts.find(c => 
      c.employee_id === employeeId && 
      ['active', 'signed'].includes(c.status)
    );
  };

  const getContractExpiryWarning = (contract) => {
    if (!contract || !contract.end_date) return null;
    
    const today = new Date();
    const endDate = new Date(contract.end_date);
    const daysUntilExpiry = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { 
        type: 'expired', 
        days: Math.abs(daysUntilExpiry), 
        color: '#ef4444',
        message: t('Expired {days}d ago', { days: Math.abs(daysUntilExpiry) })
      };
    } else if (daysUntilExpiry <= 30) {
      return { 
        type: 'expiring', 
        days: daysUntilExpiry, 
        color: '#f59e0b',
        message: t('Expires in {days}d', { days: daysUntilExpiry })
      };
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        await employeeAPI.update(editingEmployee.id, formData);
        notify(t('Employee updated!'), 'success');
      } else {
        await employeeAPI.create(formData);
        notify(t('Employee created!'), 'success');
      }
      setShowModal(false);
      setEditingEmployee(null);
      resetForm();
      loadEmployees();
    } catch (error) {
      notify(error.response?.data?.message || t('Error saving employee'), 'error');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadPhoto = async (employeeId) => {
    if (!selectedFile) {
      notify(t('Please select a photo'), 'warning');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('profile_picture', selectedFile);

      await employeeAPI.uploadProfilePicture(employeeId, formData);
      notify(t('Photo uploaded successfully!'), 'success');
      setSelectedFile(null);
      loadEmployees();
      
      // Refresh detail modal if open
      if (selectedEmployee?.id === employeeId) {
        const response = await employeeAPI.getById(employeeId);
        setSelectedEmployee(response.data.employee);
      }
    } catch (error) {
      notify(t('Error uploading photo'), 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!await confirmDialog(t('Delete this employee?'))) return;
    
    try {
      await employeeAPI.delete(id);
      notify(t('Employee deleted'), 'success');
      loadEmployees();
    } catch (error) {
      // 409: someone with payroll, attendance, leave... is set to Inactive, not deleted.
      notify(error.response?.data?.code === 'EMPLOYEE_HAS_HISTORY'
        ? t('This employee has payroll, attendance, leave, documents, contracts or shifts and cannot be deleted. Set their status to Inactive instead.')
        : t('Error deleting employee'), 'error');
    }
  };

  const openEditModal = (employee) => {
    setEditingEmployee(employee);
    setShowModal(true);
    setFormData({
      first_name: employee.first_name,
      last_name: employee.last_name,
      email: employee.email,
      phone: employee.phone || '',
      address: employee.address || '',
      date_of_birth: toDateInputValue(employee.date_of_birth),
      hire_date: toDateInputValue(employee.hire_date),
      position: employee.position,
      department: employee.department || '',
      salary: employee.salary || '',
      currency: employee.currency || 'SRD',
      payment_type: employee.payment_type || 'monthly',
      hourly_rate: employee.hourly_rate || '',
      overtime_rate_multiplier: employee.overtime_rate_multiplier || '1.5',
      sunday_rate_multiplier: employee.sunday_rate_multiplier || '1.5',      // ⭐ AGREGAR
      holiday_rate_multiplier: employee.holiday_rate_multiplier || '2.0',    // ⭐ AGREGAR
      late_deduction_percent: employee.late_deduction_percent || '10.0', 
      status: employee.status,
      emergency_contact_name: employee.emergency_contact_name || '',
      emergency_contact_phone: employee.emergency_contact_phone || '',
      id_passport_number: employee.id_passport_number || '',
      drivers_license: employee.drivers_license || ''
    });
  };

  const openDetailModal = async (employee) => {
    try {
      const response = await employeeAPI.getById(employee.id);
      setSelectedEmployee(response.data.employee);
      setShowDetailModal(true);
      loadEmployeeDeductions(employee.id);
    } catch (error) {
      notify(t('Error loading employee details'), 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      date_of_birth: '',
      hire_date: '',
      position: '',
      department: '',
      salary: '',
      currency: 'SRD',
      payment_type: 'monthly',
      hourly_rate: '',
      overtime_rate_multiplier: '1.5',
      status: 'active',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      id_passport_number: '',
      drivers_license: ''
    });
  };

  const getProfileImage = (employee) => {
  if (employee.profile_picture) {
    const base = window.location.origin;
    return base + employee.profile_picture;
  }
  return null;
};
  // 'on_leave' is a value, not a text: in English t() would show it as it is
  const statusText = (status) => t(status === 'on_leave' ? 'On Leave' : status);

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = 
      emp.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.position?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || emp.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const canManage = ['super_admin', 'hr_manager', 'finance_manager'].includes(user?.role);

  // Deductions functions
  const loadEmployeeDeductions = async (employeeId) => {
    try {
      const response = await employeeAPI.getDeductions(employeeId);
      if (response.data.success) {
        setEmployeeDeductions(response.data.deductions);
      }
    } catch (error) {
      console.error('Error loading deductions:', error);
    }
  };
  
  const loadAvailableDeductions = async (employeeId) => {
    try {
      const response = await employeeAPI.getAvailableDeductions(employeeId);
      if (response.data.success) {
        setAvailableDeductions(response.data.deductions);
      }
    } catch (error) {
      console.error('Error loading available deductions:', error);
    }
  };
  
  const handleAddDeduction = () => {
    setEditingDeduction(null);
    setDeductionForm({
      deduction_id: '',
      custom_amount: '',
      start_date: '',
      end_date: ''
    });
    loadAvailableDeductions(selectedEmployee.id);
    setShowDeductionModal(true);
  };
  
  const handleEditDeduction = (deduction) => {
    setEditingDeduction(deduction);
    setDeductionForm({
      deduction_id: deduction.deduction_id,
      custom_amount: deduction.custom_amount,
      start_date: toDateInputValue(deduction.start_date),
      end_date: toDateInputValue(deduction.end_date)
    });
    setShowDeductionModal(true);
  };
  
  const handleSaveDeduction = async () => {
    try {
      const body = editingDeduction
        ? {
            custom_amount: parseFloat(deductionForm.custom_amount),
            is_active: true,
            start_date: deductionForm.start_date || null,
            end_date: deductionForm.end_date || null
          }
        : {
            deduction_id: deductionForm.deduction_id,
            custom_amount: parseFloat(deductionForm.custom_amount),
            start_date: deductionForm.start_date || null,
            end_date: deductionForm.end_date || null
          };

      const response = editingDeduction
        ? await employeeAPI.updateDeduction(selectedEmployee.id, editingDeduction.id, body)
        : await employeeAPI.addDeduction(selectedEmployee.id, body);
  
      if (response.data.success) {
        notify(editingDeduction ? t('Deduction updated!') : t('Deduction added!'), 'success');
        setShowDeductionModal(false);
        loadEmployeeDeductions(selectedEmployee.id);
      } else {
        notify(t('Error: {message}', { message: response.data.message }), 'error');
      }
    } catch (error) {
      console.error('Error saving deduction:', error);
      notify(t('Error saving deduction'), 'error');
    }
  };
  
  const handleToggleDeduction = async (deduction) => {
    if (!await confirmDialog(t(deduction.is_active ? 'Deactivate this deduction?' : 'Activate this deduction?'))) {
      return;
    }
  
    try {
      const response = await employeeAPI.updateDeduction(selectedEmployee.id, deduction.id, {
            custom_amount: deduction.custom_amount,
            is_active: !deduction.is_active,
            start_date: deduction.start_date,
            end_date: deduction.end_date
      });
  
      if (response.data.success) {
        loadEmployeeDeductions(selectedEmployee.id);
      } else {
        notify(t('Error: {message}', { message: response.data.message }), 'error');
      }
    } catch (error) {
      console.error('Error toggling deduction:', error);
      notify(t('Error toggling deduction'), 'error');
    }
  };
  
  const handleRemoveDeduction = async (deduction) => {
    if (!await confirmDialog(t('Remove "{name}" deduction?', { name: deduction.name }))) {
      return;
    }
  
    try {
      const response = await employeeAPI.removeDeduction(selectedEmployee.id, deduction.id);
  
      if (response.data.success) {
        notify(t('Deduction removed!'), 'success');
        loadEmployeeDeductions(selectedEmployee.id);
      } else {
        notify(t('Error: {message}', { message: response.data.message }), 'error');
      }
    } catch (error) {
      console.error('Error removing deduction:', error);
      notify(t('Error removing deduction'), 'error');
    }
  };
  
  const closeForm = () => {
    setShowModal(false);
    setEditingEmployee(null);
    resetForm();
  };
  const closeDetail = () => {
    setShowDetailModal(false);
    setSelectedEmployee(null);
    setSelectedFile(null);
  };
  const avatar = (emp, size) => (
    <span className={`emp-avatar${size ? ` emp-avatar--${size}` : ''}`}>
      {getProfileImage(emp)
        ? <img src={getProfileImage(emp)} alt={size === 'lg' ? `${emp.first_name} ${emp.last_name}` : ''} />
        : getInitials(emp.first_name, emp.last_name)}
    </span>
  );
  const rowActions = (emp) => (
    // Inside a clickable card or row: the click stays on the button
    <div className="emp-actions" onClick={(e) => e.stopPropagation()}>
      {canEditEmployee && (
        <button type="button" onClick={() => openEditModal(emp)} className="btn btn--secondary btn--icon" title={t('Edit')} aria-label={t('Edit')}>
          <Pencil aria-hidden="true" />
        </button>
      )}
      {canDeleteEmployee && (
        <button type="button" onClick={() => handleDelete(emp.id)} className="btn btn--secondary btn--icon btn--danger-text" title={t('Delete')} aria-label={t('Delete')}>
          <Trash2 aria-hidden="true" />
        </button>
      )}
    </div>
  );

  return (
    <div className="employees-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Employees')}</h1>
          <p className="page-sub">{t('Manage your workforce')}</p>
        </div>
        <div className="page-actions">
          <div className="emp-view-toggle">
            <button
              type="button"
              className={`btn btn--secondary btn--icon${viewMode === 'grid' ? ' is-active' : ''}`}
              onClick={() => setViewMode('grid')}
              title={t('Grid View')}
              aria-label={t('Grid View')}
              aria-pressed={viewMode === 'grid'}
            >
              <LayoutGrid aria-hidden="true" />
            </button>
            <button
              type="button"
              className={`btn btn--secondary btn--icon${viewMode === 'table' ? ' is-active' : ''}`}
              onClick={() => setViewMode('table')}
              title={t('Table View')}
              aria-label={t('Table View')}
              aria-pressed={viewMode === 'table'}
            >
              <List aria-hidden="true" />
            </button>
          </div>
          {canManage && (
            <button type="button" onClick={() => setShowModal(true)} className="btn btn--primary">
              <Plus aria-hidden="true" /> {t('Add Employee')}
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="emp-filters">
        <label className="emp-search">
          <Search aria-hidden="true" />
          <input
            type="text"
            className="input"
            placeholder={t('Search employees...')}
            aria-label={t('Search employees...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </label>
        <select
          className="select emp-status-filter"
          aria-label={t('Status')}
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">{t('All Status')}</option>
          <option value="active">{t('Active')}</option>
          <option value="inactive">{t('Inactive')}</option>
          <option value="on_leave">{t('On Leave')}</option>
        </select>
      </div>

      {/* Employees List */}
      {loading ? (
        <div className="empty">{t('Loading employees...')}</div>
      ) : filteredEmployees.length === 0 ? (
        <div className="empty">
          <p>{t('No employees found')}</p>
          {canCreateEmployee && (
            <button type="button" onClick={() => setShowModal(true)} className="btn btn--primary">
              {t('Add First Employee')}
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="emp-grid">
          {filteredEmployees.map(emp => (
            <div key={emp.id} className="emp-card" onClick={() => openDetailModal(emp)}>
              <div className="emp-card__top">
                {avatar(emp)}
                <div className="emp-card__who">
                  <h3 className="emp-card__name">{emp.first_name} {emp.last_name}</h3>
                  <p className="emp-card__position">{emp.position}</p>
                </div>
                {emp.employee_id && <span className="emp-card__id">{emp.employee_id}</span>}
              </div>
              <ul className="emp-card__facts">
                {emp.department && <li><Building2 aria-hidden="true" />{t(emp.department)}</li>}
                <li><Mail aria-hidden="true" />{emp.email}</li>
                {emp.phone && <li><Phone aria-hidden="true" />{emp.phone}</li>}
              </ul>
              <div className="emp-card__foot">
                <StatusBadge status={emp.status}>{statusText(emp.status)}</StatusBadge>
                {(canEditEmployee || canDeleteEmployee) && rowActions(emp)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="emp-table-wrap">
          <table className="data-table emp-table">
            <thead>
              <tr>
                <th>{t('Employee ID')}</th>
                <th>{t('Photo')}</th>
                <th>{t('Name')}</th>
                <th>{t('Position')}</th>
                <th>{t('Department')}</th>
                <th>{t('Email')}</th>
                <th>{t('Phone')}</th>
                <th>{t('Status')}</th>
                <th>{t('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map(emp => (
                <tr key={emp.id} onClick={() => openDetailModal(emp)}>
                  <td className="emp-id">{emp.employee_id || t('N/A')}</td>
                  <td>{avatar(emp, 'sm')}</td>
                  <td className="emp-name">{emp.first_name} {emp.last_name}</td>
                  <td>{emp.position}</td>
                  <td>{emp.department ? t(emp.department) : '-'}</td>
                  <td>{emp.email}</td>
                  <td>{emp.phone || '-'}</td>
                  <td>
                    <StatusBadge status={emp.status}>{statusText(emp.status)}</StatusBadge>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {(canEditEmployee || canDeleteEmployee) && rowActions(emp)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ========================================= */}
      {/* CREATE/EDIT MODAL */}
      {/* ========================================= */}
      <Modal
        open={showModal}
        title={editingEmployee ? t('Edit Employee') : t('Create New Employee')}
        onClose={closeForm}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="emp-dialog emp-form">
          {/* ========== PERSONAL INFORMATION ========== */}
          <section className="emp-section">
            <h3 className="emp-section__title"><User aria-hidden="true" /> {t('Personal Information')}</h3>

            <div className="emp-form-row">
              <div className="emp-field">
                <label className="field-label">{t('First Name *')}</label>
                <input
                  type="text"
                  name="first_name"
                  className="input"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  required
                  placeholder={t('Enter first name')}
                />
              </div>

              <div className="emp-field">
                <label className="field-label">{t('Last Name *')}</label>
                <input
                  type="text"
                  name="last_name"
                  className="input"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  required
                  placeholder={t('Enter last name')}
                />
              </div>
            </div>

            <div className="emp-form-row">
              <div className="emp-field">
                <label className="field-label">{t('Email *')}</label>
                <input
                  type="email"
                  name="email"
                  className="input"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  placeholder="employee@company.com"
                />
              </div>

              <div className="emp-field">
                <label className="field-label">{t('Phone Number')}</label>
                <input
                  type="tel"
                  name="phone"
                  className="input"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+597 123-4567"
                />
              </div>
            </div>

            <div className="emp-form-row">
              <div className="emp-field">
                <label className="field-label">{t('Date of Birth')}</label>
                <div className="emp-inline">
                  <input
                    type="date"
                    name="date_of_birth"
                    className="input"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                    max={toLocalISODate(new Date())}
                  />
                  {formData.date_of_birth && (
                    <button
                      type="button"
                      className="btn btn--secondary btn--icon btn--danger-text"
                      onClick={() => setFormData({...formData, date_of_birth: ''})}
                      title={t('Clear date')}
                      aria-label={t('Clear date')}
                    >
                      <X aria-hidden="true" />
                    </button>
                  )}
                </div>
                <small className="field-hint">
                  {t("Optional - Employee's date of birth")}
                </small>
              </div>

              <div className="emp-field">
                <label className="field-label">{t('Address')}</label>
                <input
                  type="text"
                  name="address"
                  className="input"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder={t('Enter complete address')}
                />
              </div>
            </div>
          </section>

          {/* ========== IDENTIFICATION DOCUMENTS ========== */}
          <section className="emp-section">
            <h3 className="emp-section__title"><IdCard aria-hidden="true" /> {t('Identification Documents')}</h3>

            <div className="emp-form-row">
              <div className="emp-field">
                <label className="field-label">{t('ID / Passport Number')}</label>
                <input
                  type="text"
                  name="id_passport_number"
                  className="input"
                  value={formData.id_passport_number}
                  onChange={(e) => setFormData({...formData, id_passport_number: e.target.value})}
                  placeholder={t('Enter ID or Passport number')}
                />
              </div>

              <div className="emp-field">
                <label className="field-label">{t("Driver's License")}</label>
                <input
                  type="text"
                  name="drivers_license"
                  className="input"
                  value={formData.drivers_license}
                  onChange={(e) => setFormData({...formData, drivers_license: e.target.value})}
                  placeholder={t("Enter driver's license number")}
                />
              </div>
            </div>
          </section>

          {/* ========== EMERGENCY CONTACT ========== */}
          <section className="emp-section">
            <h3 className="emp-section__title"><Siren aria-hidden="true" /> {t('Emergency Contact')}</h3>

            <div className="emp-form-row">
              <div className="emp-field">
                <label className="field-label">{t('Contact Name')}</label>
                <input
                  type="text"
                  name="emergency_contact_name"
                  className="input"
                  value={formData.emergency_contact_name}
                  onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})}
                  placeholder={t('Emergency contact name')}
                />
              </div>

              <div className="emp-field">
                <label className="field-label">{t('Contact Phone')}</label>
                <input
                  type="tel"
                  name="emergency_contact_phone"
                  className="input"
                  value={formData.emergency_contact_phone}
                  onChange={(e) => setFormData({...formData, emergency_contact_phone: e.target.value})}
                  placeholder="+597 123-4567"
                />
              </div>
            </div>
          </section>

          {/* ========== EMPLOYMENT INFORMATION ========== */}
          <section className="emp-section">
            <h3 className="emp-section__title"><Briefcase aria-hidden="true" /> {t('Employment Information')}</h3>

            <div className="emp-form-row">
              <div className="emp-field">
                <label className="field-label">{t('Department *')}</label>
                <select
                  name="department"
                  className="select"
                  value={formData.department}
                  onChange={(e) => setFormData({...formData, department: e.target.value})}
                  required
                >
                  <option value="">{t('Select Department')}</option>
                  <option value="Security">{t('Security')}</option>
                  <option value="Administration">{t('Administration')}</option>
                  <option value="Operations">{t('Operations')}</option>
                  <option value="Finance">{t('Finance')}</option>
                  <option value="HR">{t('Human Resources')}</option>
                  <option value="IT">{t('IT')}</option>
                </select>
              </div>

              <div className="emp-field">
                <label className="field-label">{t('Position *')}</label>
                <input
                  type="text"
                  name="position"
                  className="input"
                  value={formData.position}
                  onChange={(e) => setFormData({...formData, position: e.target.value})}
                  required
                  placeholder={t('Job title')}
                />
              </div>
            </div>

            <div className="emp-form-row">
              <div className="emp-field">
                <label className="field-label">{t('Hire Date *')}</label>
                <input
                  type="date"
                  name="hire_date"
                  className="input"
                  value={formData.hire_date}
                  onChange={(e) => setFormData({...formData, hire_date: e.target.value})}
                  required
                  disabled={editingEmployee ? true : false}
                  max={toLocalISODate(new Date())}
                />
                {editingEmployee ? (
                  <small className="field-hint emp-hint-icon">
                    <Lock aria-hidden="true" /> {t('Hire date cannot be changed after employee is created')}
                  </small>
                ) : (
                  <small className="field-hint">
                    {t('Date when employee was hired')}
                  </small>
                )}
              </div>

              <div className="emp-field">
                <label className="field-label">{t('Payment Type *')}</label>
                <select
                  name="payment_type"
                  className="select"
                  value={formData.payment_type}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      payment_type: e.target.value,
                      salary: e.target.value === 'hourly' ? '' : formData.salary,
                      hourly_rate: e.target.value === 'monthly' ? '' : formData.hourly_rate
                    });
                  }}
                  required
                >
                  <option value="monthly">{t('Monthly Salary')}</option>
                  <option value="hourly">{t('Hourly Rate')}</option>
                </select>
              </div>
            </div>

            <div className="emp-form-row">
              {formData.payment_type === 'monthly' ? (
                <div className="emp-field">
                  <label className="field-label">{t('Monthly Salary *')}</label>
                  <input
                    type="number"
                    name="salary"
                    className="input"
                    value={formData.salary}
                    onChange={(e) => setFormData({...formData, salary: e.target.value})}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                  />
                  <small className="field-hint">{t('Fixed monthly salary amount')}</small>
                </div>
              ) : (
                <>
                  <div className="emp-field">
                    <label className="field-label">{t('Hourly Rate (SRD) *')}</label>
                    <input
                      type="number"
                      name="hourly_rate"
                      className="input"
                      value={formData.hourly_rate}
                      onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                      placeholder="53.00"
                      step="0.01"
                      min="0"
                      required
                    />
                    <small className="field-hint">{t('Amount paid per hour worked')}</small>
                  </div>

                  <div className="emp-field">
                    <label className="field-label">{t('Overtime Multiplier')}</label>
                    <input
                      type="number"
                      name="overtime_rate_multiplier"
                      className="input"
                      value={formData.overtime_rate_multiplier}
                      onChange={(e) => setFormData({...formData, overtime_rate_multiplier: e.target.value})}
                      placeholder="1.5"
                      step="0.1"
                      min="1"
                      max="3"
                    />
                    <small className="field-hint">{t('Multiplier for overtime pay (e.g., 1.5 = time and a half)')}</small>
                  </div>
                </>
              )}
            </div>

            {/* Premium rates, for both payment types */}
            <div className="emp-form-row">
              <div className="emp-field">
                <label className="field-label">{t('Sunday Rate Multiplier *')}</label>
                <input
                  type="number"
                  className="input"
                  value={formData.sunday_rate_multiplier}
                  onChange={(e) => setFormData({...formData, sunday_rate_multiplier: e.target.value})}
                  step="0.1"
                  min="1.0"
                  max="5.0"
                  required
                />
                <small className="field-hint">{t('Multiplier for Sunday work (default: 1.5x = time and a half)')}</small>
              </div>

              <div className="emp-field">
                <label className="field-label">{t('Holiday Rate Multiplier *')}</label>
                <input
                  type="number"
                  className="input"
                  value={formData.holiday_rate_multiplier}
                  onChange={(e) => setFormData({...formData, holiday_rate_multiplier: e.target.value})}
                  step="0.1"
                  min="1.0"
                  max="5.0"
                  required
                />
                <small className="field-hint">{t('Multiplier for Holiday work (default: 2.0x = double time)')}</small>
              </div>
            </div>

            <div className="emp-form-row">
              <div className="emp-field">
                <label className="field-label">{t('Late Deduction % *')}</label>
                <input
                  type="number"
                  className="input"
                  value={formData.late_deduction_percent}
                  onChange={(e) => setFormData({...formData, late_deduction_percent: e.target.value})}
                  step="0.1"
                  min="0"
                  max="100"
                  required
                />
                <small className="field-hint">{t("Percentage to deduct from day's pay when late (default: 10%)")}</small>
              </div>

              <div className="emp-field">
                <label className="field-label">{t('Currency *')}</label>
                <select
                  name="currency"
                  className="select"
                  value={formData.currency}
                  onChange={(e) => setFormData({...formData, currency: e.target.value})}
                  required
                >
                  <option value="SRD">{t('SRD - Surinamese Dollar')}</option>
                  <option value="USD">{t('USD - US Dollar')}</option>
                </select>
                <small className="field-hint">{t("Select the currency for this employee's payment")}</small>
              </div>
            </div>

            <div className="emp-field">
              <label className="field-label">{t('Status *')}</label>
              <select
                name="status"
                className="select"
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                required
              >
                <option value="active">{t('Active')}</option>
                <option value="inactive">{t('Inactive')}</option>
                <option value="on_leave">{t('On Leave')}</option>
                <option value="terminated">{t('Terminated')}</option>
              </select>
            </div>
          </section>

          <div className="emp-form-actions">
            <button type="button" className="btn btn--secondary" onClick={closeForm}>
              {t('Cancel')}
            </button>
            <button type="submit" className="btn btn--primary">
              {editingEmployee
                ? <><Save aria-hidden="true" /> {t('Update Employee')}</>
                : <><Plus aria-hidden="true" /> {t('Create Employee')}</>}
            </button>
          </div>
        </form>
      </Modal>

      {/* ========================================= */}
      {/* DETAIL MODAL */}
      {/* ========================================= */}
      <Modal
        open={showDetailModal && Boolean(selectedEmployee)}
        title={t('Employee Details')}
        onClose={closeDetail}
        size="lg"
      >
        {selectedEmployee && (
          <div className="emp-dialog emp-detail">
            {/* PROFILE SECTION WITH PHOTO */}
            <div className="emp-profile">
              {avatar(selectedEmployee, 'lg')}
              <div className="emp-profile__info">
                <h2 className="emp-profile__name">{selectedEmployee.first_name} {selectedEmployee.last_name}</h2>
                <p className="emp-profile__position">{selectedEmployee.position}</p>
                {selectedEmployee.department && (
                  <p className="emp-profile__department"><Building2 aria-hidden="true" /> {t(selectedEmployee.department)}</p>
                )}
                <StatusBadge status={selectedEmployee.status}>
                  {statusText(selectedEmployee.status)}
                </StatusBadge>
              </div>

              {/* PHOTO UPLOAD SECTION */}
              {canEditEmployee && (
                <div className="emp-photo">
                  <h3 className="emp-section__title"><Camera aria-hidden="true" /> {t('Update Profile Photo')}</h3>
                  <div className="emp-photo__controls">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleFileChange}
                      id="photo-upload"
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="photo-upload" className="btn btn--secondary">
                      {t('Choose Photo')}
                    </label>
                    {selectedFile && (
                      <div className="emp-photo__file">
                        <span><Paperclip aria-hidden="true" /> {selectedFile.name}</span>
                        <button
                          type="button"
                          onClick={() => handleUploadPhoto(selectedEmployee.id)}
                          className="btn btn--primary"
                        >
                          <Upload aria-hidden="true" /> {t('Upload')}
                        </button>
                      </div>
                    )}
                  </div>
                  <small className="field-hint">{t('JPG or PNG, max 5MB')}</small>
                </div>
              )}
            </div>

            {/* PERSONAL INFO */}
            <section className="emp-section">
              <h3 className="emp-section__title"><User aria-hidden="true" /> {t('Personal Information')}</h3>
              <dl className="emp-detail-grid">
                {selectedEmployee.employee_id && (
                  <div className="emp-detail-item">
                    <dt>{t('Employee ID:')}</dt>
                    <dd>{selectedEmployee.employee_id}</dd>
                  </div>
                )}
                <div className="emp-detail-item">
                  <dt>{t('Email:')}</dt>
                  <dd>{selectedEmployee.email}</dd>
                </div>
                {selectedEmployee.phone && (
                  <div className="emp-detail-item">
                    <dt>{t('Phone:')}</dt>
                    <dd>{selectedEmployee.phone}</dd>
                  </div>
                )}
                {selectedEmployee.date_of_birth && (
                  <div className="emp-detail-item">
                    <dt>{t('Date of Birth:')}</dt>
                    <dd>{parseLocalDate(selectedEmployee.date_of_birth)?.toLocaleDateString(locale)}</dd>
                  </div>
                )}
                {selectedEmployee.address && (
                  <div className="emp-detail-item emp-detail-item--wide">
                    <dt>{t('Address:')}</dt>
                    <dd>{selectedEmployee.address}</dd>
                  </div>
                )}
              </dl>
            </section>

            {/* IDENTIFICATION DOCUMENTS */}
            {(selectedEmployee.id_passport_number || selectedEmployee.drivers_license) && (
              <section className="emp-section">
                <h3 className="emp-section__title"><IdCard aria-hidden="true" /> {t('Identification Documents')}</h3>
                <dl className="emp-detail-grid">
                  {selectedEmployee.id_passport_number && (
                    <div className="emp-detail-item">
                      <dt>{t('ID / Passport:')}</dt>
                      <dd>{selectedEmployee.id_passport_number}</dd>
                    </div>
                  )}
                  {selectedEmployee.drivers_license && (
                    <div className="emp-detail-item">
                      <dt>{t("Driver's License:")}</dt>
                      <dd>{selectedEmployee.drivers_license}</dd>
                    </div>
                  )}
                </dl>
              </section>
            )}

            {/* EMERGENCY CONTACT */}
            {(selectedEmployee.emergency_contact_name || selectedEmployee.emergency_contact_phone) && (
              <section className="emp-section">
                <h3 className="emp-section__title"><Siren aria-hidden="true" /> {t('Emergency Contact')}</h3>
                <dl className="emp-detail-grid">
                  {selectedEmployee.emergency_contact_name && (
                    <div className="emp-detail-item">
                      <dt>{t('Contact Name:')}</dt>
                      <dd>{selectedEmployee.emergency_contact_name}</dd>
                    </div>
                  )}
                  {selectedEmployee.emergency_contact_phone && (
                    <div className="emp-detail-item">
                      <dt>{t('Contact Phone:')}</dt>
                      <dd>{selectedEmployee.emergency_contact_phone}</dd>
                    </div>
                  )}
                </dl>
              </section>
            )}

            {/* EMPLOYMENT INFO */}
            <section className="emp-section">
              <h3 className="emp-section__title"><Briefcase aria-hidden="true" /> {t('Employment Information')}</h3>
              <dl className="emp-detail-grid">
                <div className="emp-detail-item">
                  <dt>{t('Position:')}</dt>
                  <dd>{selectedEmployee.position}</dd>
                </div>
                {selectedEmployee.department && (
                  <div className="emp-detail-item">
                    <dt>{t('Department:')}</dt>
                    <dd>{t(selectedEmployee.department)}</dd>
                  </div>
                )}
                <div className="emp-detail-item">
                  <dt>{t('Hire Date:')}</dt>
                  <dd>{parseLocalDate(selectedEmployee.hire_date)?.toLocaleDateString(locale)}</dd>
                </div>
                {/* Not sent to roles that may only view employees (no employees:update
                    or payroll:view), and empty for some staff: then no line, not "$NaN". */}
                {selectedEmployee.salary !== undefined && selectedEmployee.salary !== null && selectedEmployee.salary !== '' && (
                  <div className="emp-detail-item">
                    <dt>{t('Salary:')}</dt>
                    <dd className="num">{formatCurrency(selectedEmployee.salary, selectedEmployee.currency || 'SRD', locale)}</dd>
                  </div>
                )}
                <div className="emp-detail-item">
                  <dt>{t('Status:')}</dt>
                  <dd>
                    <StatusBadge status={selectedEmployee.status}>
                      {statusText(selectedEmployee.status)}
                    </StatusBadge>
                  </dd>
                </div>
              </dl>
            </section>

            {/* GUARD PORTAL ACCOUNT (employees:update, as the API asks) */}
            {can(user, 'employees', 'update') && <PortalAccessSection employeeId={selectedEmployee.id} />}

            {/* DEDUCTIONS SECTION */}
            <section className="emp-section">
              <div className="emp-section__head">
                <h3 className="emp-section__title"><Receipt aria-hidden="true" /> {t('Deductions')}</h3>
                {canManageDeductions && (
                  <button type="button" onClick={handleAddDeduction} className="btn btn--secondary">
                    {t('+ Add Deduction')}
                  </button>
                )}
              </div>

              {employeeDeductions.length === 0 ? (
                <p className="emp-no-data">{t('No deductions assigned')}</p>
              ) : (
                <ul className="emp-deductions">
                  {employeeDeductions.map(deduction => (
                    <li
                      key={deduction.id}
                      className={`emp-deduction${deduction.is_active ? '' : ' is-inactive'}`}
                    >
                      <div className="emp-deduction__info">
                        <div className="emp-deduction__name">
                          {deduction.name}
                          <StatusBadge status={deduction.is_active ? 'active' : 'inactive'}>
                            {deduction.is_active ? t('Active') : t('Inactive')}
                          </StatusBadge>
                        </div>
                        <div className="emp-deduction__details">
                          <span className="emp-deduction__amount num">
                            SRD {parseFloat(deduction.custom_amount).toLocaleString(locale, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </span>
                          <span className="emp-deduction__type">({t(deduction.type)})</span>
                        </div>
                        {(deduction.start_date || deduction.end_date) && (
                          <div className="emp-deduction__dates">
                            {deduction.start_date && (
                              <span>{t('From:')} {parseLocalDate(deduction.start_date)?.toLocaleDateString(locale)}</span>
                            )}
                            {deduction.end_date && (
                              <span>{t('To:')} {parseLocalDate(deduction.end_date)?.toLocaleDateString(locale)}</span>
                            )}
                          </div>
                        )}
                      </div>

                      {canManage && (
                        <div className="emp-actions">
                          <button
                            type="button"
                            onClick={() => handleEditDeduction(deduction)}
                            className="btn btn--secondary btn--icon"
                            title={t('Edit')}
                            aria-label={t('Edit')}
                          >
                            <Pencil aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleToggleDeduction(deduction)}
                            className="btn btn--secondary btn--icon"
                            title={deduction.is_active ? t('Deactivate') : t('Activate')}
                            aria-label={deduction.is_active ? t('Deactivate') : t('Activate')}
                          >
                            {deduction.is_active ? <PowerOff aria-hidden="true" /> : <Power aria-hidden="true" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveDeduction(deduction)}
                            className="btn btn--secondary btn--icon btn--danger-text"
                            title={t('Remove')}
                            aria-label={t('Remove')}
                          >
                            <Trash2 aria-hidden="true" />
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* QUICK LINKS */}
            <section className="emp-section">
              <h3 className="emp-section__title"><Link aria-hidden="true" /> {t('Quick Actions')}</h3>
              <div className="emp-quick-links">
                <button
                  type="button"
                  onClick={() => {
                    setShowDetailModal(false);
                    window.location.href = `/documents?employee_id=${selectedEmployee.id}`;
                  }}
                  className="btn btn--secondary"
                >
                  <FolderOpen aria-hidden="true" /> {t('View Documents')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDetailModal(false);
                    window.location.href = `/contracts?employee_id=${selectedEmployee.id}`;
                  }}
                  className="btn btn--secondary"
                >
                  <ScrollText aria-hidden="true" /> {t('View Contracts')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDetailModal(false);
                    window.location.href = `/attendance?employee_id=${selectedEmployee.id}`;
                  }}
                  className="btn btn--secondary"
                >
                  <Clock aria-hidden="true" /> {t('View Attendance')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDetailModal(false);
                    window.location.href = `/payroll?employee_id=${selectedEmployee.id}`;
                  }}
                  className="btn btn--secondary"
                >
                  <Wallet aria-hidden="true" /> {t('View Payroll')}
                </button>
              </div>
            </section>

            <div className="emp-form-actions">
              {canEditEmployee && (
                <button
                  type="button"
                  onClick={() => {
                    setShowDetailModal(false);
                    openEditModal(selectedEmployee);
                  }}
                  className="btn btn--primary"
                >
                  <Pencil aria-hidden="true" /> {t('Edit Employee')}
                </button>
              )}
              <button type="button" onClick={closeDetail} className="btn btn--secondary">
                {t('Close')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ========================================= */}
      {/* DEDUCTION MODAL */}
      {/* ========================================= */}
      <Modal
        open={showDeductionModal}
        title={editingDeduction ? t('Edit Deduction') : t('Add Deduction')}
        onClose={() => setShowDeductionModal(false)}
        footer={(
          <>
            <button type="button" onClick={() => setShowDeductionModal(false)} className="btn btn--secondary">
              {t('Cancel')}
            </button>
            <button
              type="button"
              onClick={handleSaveDeduction}
              className="btn btn--primary"
              disabled={(!deductionForm.deduction_id && !editingDeduction) || !deductionForm.custom_amount}
            >
              {editingDeduction ? t('Update Deduction') : t('Add Deduction')}
            </button>
          </>
        )}
      >
        <div className="emp-dialog emp-form-row">
          {!editingDeduction && (
            <div className="emp-field emp-field--wide">
              <label className="field-label">{t('Deduction Type *')}</label>
              <select
                className="select"
                value={deductionForm.deduction_id}
                onChange={(e) => {
                  const selectedDed = availableDeductions.find(d => d.id === parseInt(e.target.value));
                  // The catalog's default only in the employee's currency:
                  // a USD 500 default is not SRD 500.
                  const sameCurrency = selectedDed && selectedDed.currency === (selectedEmployee.currency || 'SRD');
                  setDeductionForm({
                    ...deductionForm,
                    deduction_id: e.target.value,
                    custom_amount: sameCurrency && parseFloat(selectedDed.default_amount) > 0 ? selectedDed.default_amount : ''
                  });
                }}
                required
              >
                <option value="">{t('Select deduction...')}</option>
                {availableDeductions.map(ded => (
                  <option key={ded.id} value={ded.id}>
                    {parseFloat(ded.default_amount) > 0
                      ? t('{name} (Default: {currency} {amount})', { name: ded.name, currency: ded.currency, amount: ded.default_amount })
                      : ded.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="emp-field emp-field--wide">
            <label className="field-label">{t('Amount (SRD) *')}</label>
            <input
              type="number"
              className="input"
              step="0.01"
              value={deductionForm.custom_amount}
              onChange={(e) => setDeductionForm({...deductionForm, custom_amount: e.target.value})}
              placeholder="500.00"
              required
            />
          </div>

          <div className="emp-field">
            <label className="field-label">{t('Start Date')}</label>
            <input
              type="date"
              className="input"
              value={deductionForm.start_date}
              onChange={(e) => setDeductionForm({...deductionForm, start_date: e.target.value})}
            />
            <small className="field-hint">{t('Optional - when deduction starts')}</small>
          </div>

          <div className="emp-field">
            <label className="field-label">{t('End Date')}</label>
            <input
              type="date"
              className="input"
              value={deductionForm.end_date}
              onChange={(e) => setDeductionForm({...deductionForm, end_date: e.target.value})}
            />
            <small className="field-hint">{t('Optional - when deduction ends')}</small>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Employees;
